// src/modals/EditEventModal.jsx (NEW FILE)

import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "react-hot-toast";
import axiosInstance from "../context/axiosInstance";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import LocationPicker from "./LocationPicker"; 

import {
  X,
  Loader2,
  Save,
  UserPlus,
  UserCheck,
  XCircle,
  Users,
} from "lucide-react";
import moment from "moment";

// ... imports

// Reusable input component
const FormInput = (props) => (
  <input
    {...props}
    className="w-full h-11 bg-white/5 border border-white/10 rounded-lg px-4 text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none transition-colors hover:bg-white/10"
  />
);

const FormSection = ({ icon, title, children }) => (
  <fieldset className="border-t border-white/10 pt-6">
    <legend className="text-sm font-semibold text-gray-300 mb-4 flex items-center gap-2">
      {icon} {title}
    </legend>
    {children}
  </fieldset>
);

const EditEventModal = ({ event, onClose, onEventUpdated }) => {
  const [eventData, setEventData] = useState({
    eventName: event.eventName || "",
    eventDate: event.eventDateTime ? moment(event.eventDateTime).format("YYYY-MM-DD") : "",
    eventTime: event.eventDateTime ? moment(event.eventDateTime).format("HH:mm") : "",
    description: event.description || "",
    inviteExpiryDate: event.inviteExpiryDate ? moment(event.inviteExpiryDate).format("YYYY-MM-DD") : "",
  });

  const [location, setLocation] = useState(event.venue || null);
  const [coHosts, setCoHosts] = useState(event.co_hosts || []);
  const [coHostInput, setCoHostInput] = useState("");
  const [isVerifyingCoHost, setIsVerifyingCoHost] = useState(false);
  
  const [invitees, setInvitees] = useState([]);
  const [inviteInput, setInviteInput] = useState("");
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) =>
    setEventData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const validateAndAddCoHost = async () => {
    if (!/^\+\d{8,15}$/.test(coHostInput)) {
      return toast.error("Invalid phone number format.");
    }
    if (coHosts.some((ch) => ch.phone === coHostInput)) {
      return toast.error("Already added as co-host.");
    }

    setIsVerifyingCoHost(true);
    try {
      const { data } = await axiosInstance.get(`/users/verify-phone?phone=${encodeURIComponent(coHostInput)}`);
      if (data.user) {
        setCoHosts([...coHosts, { ...data.user, phone: coHostInput, mobile_number: coHostInput }]);
        setCoHostInput("");
        toast.success(`Verified: ${data.user.full_name}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "User not found or verification failed.");
    } finally {
      setIsVerifyingCoHost(false);
    }
  };

  const removeCoHost = (userId) => setCoHosts(coHosts.filter((ch) => ch.user_id !== userId));

  const addInvitee = () => {
    if (!/^\+\d{8,15}$/.test(inviteInput)) return toast.error("Invalid phone.");
    if (invitees.includes(inviteInput)) return toast.error("Already in list.");
    setInvitees([...invitees, inviteInput]);
    setInviteInput("");
  };

  const removeInvitee = (num) => setInvitees(invitees.filter((i) => i !== num));

  const handleSubmitChanges = async () => {
    if (!eventData.eventName || !eventData.eventDate || !eventData.eventTime) {
      return toast.error("Please fill in all required fields.");
    }

    setIsSubmitting(true);
    try {
      const formattedDateTime = moment(
        `${eventData.eventDate} ${eventData.eventTime}`,
        "YYYY-MM-DD HH:mm"
      ).toISOString();

      const payload = {
        eventData: {
          eventName: eventData.eventName,
          eventDateTime: formattedDateTime,
          description: eventData.description,
          venue: location,
          inviteExpiryDate: eventData.inviteExpiryDate,
        },
        invitees: invitees, // New phone numbers
        coHosts: coHosts,   // Full list of co-host objects
      };

      await axiosInstance.put(`/events/${event.event_id}`, payload);
      toast.success("Event updated successfully!");
      if (onEventUpdated) onEventUpdated();
      onClose();
    } catch (err) {
      console.error("Update error:", err);
      toast.error(err.response?.data?.error || "Failed to update event.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="relative w-full max-w-2xl bg-surface border border-white/10 rounded-2xl shadow-2xl p-8 max-h-[90vh] flex flex-col"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors bg-white/5 hover:bg-white/10 rounded-full p-2"
        >
          <X size={20} />
        </button>
        <h2 className="text-2xl font-bold text-white mb-6">Edit Event</h2>

        <div className="flex-grow overflow-y-auto space-y-6 pr-2 -mr-4 custom-scrollbar">
          {/* Event Details Section */}
          <div className="space-y-4">
            <FormInput
              name="eventName"
              placeholder="Event Name"
              value={eventData.eventName}
              onChange={handleChange}
              required
            />
            <div className="grid grid-cols-2 gap-4">
              <FormInput
                name="eventDate"
                type="date"
                value={eventData.eventDate}
                onChange={handleChange}
                required
              />
              <FormInput
                name="eventTime"
                type="time"
                value={eventData.eventTime}
                onChange={handleChange}
                required
              />
            </div>
            <LocationPicker
              key={event.event_id}
              initialLocation={location}
              onLocationSelect={setLocation}
            />
            <textarea
              name="description"
              placeholder="Description..."
              value={eventData.description}
              onChange={handleChange}
              rows={3}
              className="w-full bg-white/5 border border-white/10 rounded-lg p-4 text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none hover:bg-white/10 transition-colors"
            />
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Invite Response Deadline
              </label>
              <FormInput
                name="inviteExpiryDate"
                type="date"
                value={eventData.inviteExpiryDate}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Co-Hosts Section */}
          <fieldset className="border-t border-white/10 pt-6">
            <legend className="text-sm font-semibold text-gray-300 mb-4 flex items-center gap-2">
              <UserPlus size={16} /> Manage Co-Hosts
            </legend>
            <div className="bg-black/20 p-4 rounded-lg space-y-4 border border-white/5">
              <div className="flex gap-2">
                <div className="flex-grow">
                  <PhoneInput
                    defaultCountry="in"
                    value={coHostInput}
                    onChange={(p) => setCoHostInput(p)}
                    inputClassName="!w-full !bg-transparent !border-none !text-white focus:!ring-0"
                    className="flex items-center w-full bg-white/5 border border-white/10 rounded-lg px-4 text-white focus-within:ring-2 focus-within:ring-primary-500"
                  />
                </div>
                <button
                  type="button"
                  onClick={validateAndAddCoHost}
                  disabled={
                    isVerifyingCoHost || !/^\+\d{8,15}$/.test(coHostInput)
                  }
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-500 disabled:bg-gray-700/50 disabled:text-gray-500 rounded-lg font-semibold flex items-center gap-2 transition-colors text-white"
                >
                  {isVerifyingCoHost ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <UserCheck size={16} />
                  )}{" "}
                  Verify
                </button>
              </div>
              {coHosts.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {coHosts.map((coHost) => (
                    <span
                      key={coHost.user_id}
                      className="flex items-center gap-2 bg-primary-500/10 text-primary-300 border border-primary-500/20 px-3 py-1.5 rounded-full"
                    >
                      <span className="font-medium text-sm">
                        {coHost.full_name || "Unknown"}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeCoHost(coHost.user_id)}
                        className="hover:text-red-400 transition-colors"
                      >
                        <XCircle size={16} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </fieldset>
          <FormSection icon={<Users size={16} />} title="Invite More Guests">
            <div className="bg-black/20 p-4 rounded-lg border border-white/5">
              <div className="flex flex-wrap gap-2 mb-3">
                {invitees.map((num) => (
                  <span
                    key={num}
                    className="flex items-center gap-2 bg-primary-500/10 text-primary-300 border border-primary-500/20 text-sm px-2 py-1 rounded-md"
                  >
                    {num}
                    <button type="button" onClick={() => removeInvitee(num)} className="hover:text-red-400">
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
              <PhoneInput
                defaultCountry="in"
                value={inviteInput}
                onChange={(phone) => setInviteInput(phone)}
                inputProps={{
                  onKeyDown: (e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      addInvitee();
                    }
                  },
                }}
                inputClassName="!w-full !bg-transparent !border-none !text-white focus:!ring-0"
                className="flex items-center w-full bg-white/5 border border-white/10 rounded-lg px-4 text-white focus-within:ring-2 focus-within:ring-primary-500"
              />
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Enter a number and press Enter or Comma to add to the new invite
              list.
            </p>
          </FormSection>
        </div>

        <div className="mt-8 pt-6 border-t border-white/10">
          <button
            onClick={handleSubmitChanges}
            disabled={isSubmitting}
            className="w-full bg-white text-black font-bold py-3 rounded-xl text-lg hover:bg-gray-200 flex items-center justify-center gap-2 disabled:opacity-50 transition-colors shadow-lg shadow-white/5"
          >
            {isSubmitting ? (
              <Loader2 className="animate-spin" />
            ) : (
              <Save size={18} />
            )}
            {isSubmitting ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default EditEventModal;
